import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { EXPO_PUBLIC_APP_DOMAIN } from '@env';
import { isMobile, isTablet } from '../../utils/responsive';
import Layout from '../Layout';
import Button from '../Button';
import Checkbox from '../Checkbox';
import OnboardingNavBar from '../OnboardingNavBar';
import { useMessage } from '../../contexts/Messages/MessageContext';
import { handleLinkPress } from '../../utils';

interface Props {
  onAccept: () => Promise<void>;
  initialChecked?: boolean;
}

const PrivacyPolicyForm = ({ onAccept, initialChecked = false }: Props) => {
  const { setMessage } = useMessage();
  const [checked, setChecked] = useState(initialChecked);
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation();

  const handleToggle = () => {
    setChecked(!checked);
  };

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      await onAccept();
    } catch (error) {
      setMessage({ message: error.message, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <OnboardingNavBar />
      <Layout layoutWithoutMenu={true} isScrollView={isMobile}>
        <View
          style={{
            gap: isMobile ? 40 : 80,
            width: '100%',
            maxWidth: 800,
            paddingTop: isMobile ? 40 : 80,
          }}
          className="self-center"
        >
          {/* Title + body */}
          <View style={{ gap: 12 }}>
            <Text className="text-h2Mobile md:text-h2 font-[sans-600] text-neutral-950 dark:text-neutral-50">
              {t('privacyPolicy:title')}
            </Text>
            <Text className="text-[20px] leading-7 md:text-sectionSubtitle font-[sans-600] text-neutral-950 dark:text-neutral-50">
              {t('privacyPolicy:body')}{' '}
              <Text
                className="underline text-neutral-950 dark:text-neutral-50"
                onPress={() =>
                  handleLinkPress(`${EXPO_PUBLIC_APP_DOMAIN}/privacy/privacy_${i18n.language}.html`)
                }
              >
                {t('privacyPolicy:body_link')}
              </Text>
              {t('privacyPolicy:body_suffix')}
            </Text>
          </View>

          {/* Checkbox */}
          <Pressable onPress={handleToggle} className="flex-row items-center px-[6px] sm:px-0">
            <Checkbox checked={checked} classNames={'mr-2'} onPress={handleToggle} />
            <Text className="text-calendarCardTitle font-[sans-600] text-neutral-950 dark:text-neutral-50 flex-1">
              {t('privacyPolicy:checkbox_label')}
              <Text className="text-danger-500"> *</Text>
            </Text>
          </Pressable>

          {/* Accept button */}
          <Button
            text={t('privacyPolicy:accept')}
            onPress={handleAccept}
            variant="fill"
            size="medium"
            disabled={!checked || isLoading}
            style={{ alignSelf: !isMobile ? 'center' : isTablet ? 'center' : undefined }}
          />
        </View>
      </Layout>
    </View>
  );
};

export default PrivacyPolicyForm;
